import React, { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import {
  HomeIcon,
  FolderIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
  CurrencyDollarIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  TagIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChartBarIcon,
  ClockIcon,
  UsersIcon,
  CogIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  CalendarDaysIcon,
  TruckIcon,
  BriefcaseIcon,
  HeartIcon,
  ChartBarIcon as ChartIcon,
  CurrencyDollarIcon as DonationIcon,
  WalletIcon,
  XMarkIcon,
  Square3Stack3DIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline'
import { useCart } from '../hooks/useCart'
import { getBrand } from '../utils/brand'
import LanguageSwitcher from './LanguageSwitcher'
import akhiratLogo from '../assets/images/akhirat-logo.png'

function Layout() {
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { itemCount } = useCart()
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Environment brand: staging (ekubur.my / staging run.app / local dev) shows
  // EKUBUR; production (akhirat.co) and any other host show AKHIRAT.
  const { wordmark: brandWordmark } = getBrand()

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? prev.filter(m => m !== menuName)
        : [...prev, menuName]
    )
  }

  const handleLogout = () => {
    setMobileMenuOpen(false)
    logout()
    navigate('/login')
  }

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Body scroll lock + Escape-to-close when drawer is open
  useEffect(() => {
    if (!mobileMenuOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [mobileMenuOpen])

  // Dark drawer mount choreography: it must be in the DOM and painted in the
  // closed position before the slide-in class flips (otherwise no transition),
  // and it unmounts only after the exit transition so the off-screen element
  // never expands the iOS layout viewport.
  const [drawerRendered, setDrawerRendered] = useState(false)
  const [drawerShown, setDrawerShown] = useState(false)
  useEffect(() => {
    if (mobileMenuOpen) {
      setDrawerRendered(true)
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setDrawerShown(true)),
      )
      return () => cancelAnimationFrame(raf)
    }
    setDrawerShown(false)
    const timer = setTimeout(() => setDrawerRendered(false), 500) // matches duration-500
    return () => clearTimeout(timer)
  }, [mobileMenuOpen])

  // Navigation based on user role
  const getNavigationForRole = () => {
    if (!user) return []

    switch (user.role) {
      case 'ADMIN':
        // Admins get base navigation + admin navigation
        const baseNavigation = [
          { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
          { name: t('nav.cases'), href: '/cases', icon: FolderIcon },
          { name: 'Wallet', href: '/wallet', icon: WalletIcon },
          { name: t('nav.profile'), href: '/profile', icon: UserCircleIcon },
        ]
        const adminNavigation = [
          { name: t('nav.adminDashboard'), href: '/admin', icon: ShieldCheckIcon },
          { name: t('nav.userManagement'), href: '/admin/users', icon: UsersIcon },
          { name: t('nav.userDocuments'), href: '/admin/user-documents', icon: DocumentCheckIcon },
          {
            name: t('nav.providerManagement'),
            href: '/admin/providers',
            icon: UserGroupIcon,
            isSubmenu: true,
            submenuItems: [
              { name: t('nav.providerOverview'), href: '/admin/providers', icon: ChartBarIcon },
              { name: t('nav.pendingApproval'), href: '/admin/providers/pending', icon: ClockIcon },
              { name: t('nav.activeProviders'), href: '/admin/providers/active', icon: UsersIcon },
              { name: t('nav.providerAnalytics'), href: '/admin/providers/analytics', icon: ChartBarIcon },
              { name: t('nav.providerSettings'), href: '/admin/providers/settings', icon: CogIcon },
              { name: t('nav.documentVerification'), href: '/admin/providers/documents', icon: DocumentTextIcon },
            ]
          },
          {
            name: t('nav.bookingManagement'),
            href: '/admin/bookings',
            icon: BriefcaseIcon,
            isSubmenu: true,
            submenuItems: [
              { name: t('nav.bookingApproval'), href: '/admin/bookings', icon: CheckCircleIcon },
              { name: t('nav.ambulance'), href: '/bookings', icon: TruckIcon },
              { name: t('nav.burial'), href: '/bookings/burial-plot/bookings', icon: ArchiveBoxIcon },
            ]
          },
          { name: t('nav.packageManagement'), href: '/admin/packages', icon: ArchiveBoxIcon },
          { name: t('nav.bankAccounts'), href: '/admin/bank-accounts', icon: CurrencyDollarIcon },
          { name: 'Promo Codes', href: '/admin/promo-codes', icon: TagIcon },
          { name: t('nav.paymentVerification'), href: '/admin/payments', icon: CheckBadgeIcon },
          { name: t('nav.verifiedPayments'), href: '/admin/payments/verified', icon: CheckCircleIcon },
          { name: 'Integrations', href: '/admin/integrations', icon: CogIcon },
          {
            name: 'Claims',
            href: '/admin/claims',
            icon: DocumentCheckIcon,
            isSubmenu: true,
            submenuItems: [
              { name: 'Inheritances', href: '/admin/claims', icon: DocumentCheckIcon },
              { name: 'Payouts', href: '/admin/claims/payouts', icon: CurrencyDollarIcon },
            ]
          },
          {
            name: t('nav.infaqManagement'),
            href: '/admin/infaq',
            icon: HeartIcon,
            isSubmenu: true,
            submenuItems: [
              { name: t('nav.campaigns'), href: '/admin/infaq/campaigns', icon: BriefcaseIcon },
              { name: t('nav.donations'), href: '/admin/infaq/donations', icon: DonationIcon },
              { name: t('nav.analytics'), href: '/admin/infaq/analytics', icon: ChartIcon },
              { name: t('nav.maintenanceApprovals'), href: '/admin/infaq/maintenance', icon: WrenchScrewdriverIcon },
            ]
          },
        ]
        return [...baseNavigation, ...adminNavigation]
      case 'PROVIDER':
      case 'AMBULANCE_PROVIDER':
      case 'QASSADTZ':
      case 'CEMETERY_PROVIDER':
      case 'OPERATOR_TRAINING':
      case 'OPERATOR_OPERATION':
        return [
          { name: t('nav.providerDashboard'), href: '/provider-dashboard', icon: HomeIcon },
          { name: t('nav.profile'), href: '/provider-dashboard/profile', icon: UserCircleIcon },
          { name: t('nav.schedule'), href: '/provider-dashboard/schedule', icon: CalendarDaysIcon },
          { name: t('nav.assets'), href: '/provider-dashboard/assets', icon: Cog6ToothIcon },
          { name: t('nav.providerInfaq'), href: '/provider-dashboard/infaq', icon: WrenchScrewdriverIcon },
          { name: t('nav.bookings'), href: '/provider-dashboard/bookings', icon: FolderIcon },
          { name: t('nav.analytics'), href: '/provider-dashboard/analytics', icon: CheckCircleIcon },
        ]
      case 'VENDOR':
        return [
          { name: t('nav.vendorDashboard'), href: '/vendor-dashboard', icon: HomeIcon },
          { name: t('nav.products'), href: '/vendor-dashboard/products', icon: ArchiveBoxIcon },
          { name: t('nav.orders'), href: '/vendor-dashboard/orders', icon: FolderIcon },
          { name: t('nav.profile'), href: '/vendor-dashboard/profile', icon: UserCircleIcon },
        ]
      case 'FAMILY':
        return [
          { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
          {
            name: t('nav.careRequests'),
            icon: BriefcaseIcon,
            isSubmenu: true,
            submenuItems: [
              // { name: 'Funeral Cases', href: '/cases', icon: FolderIcon }, // Temporarily hidden
              { name: t('nav.ambulance'), href: '/bookings', icon: TruckIcon },
              { name: t('nav.burial'), href: '/bookings/burial-plot/bookings', icon: ArchiveBoxIcon },
            ]
          },
          { name: t('nav.newAmbulanceBooking'), href: '/bookings/ambulance/new', icon: TruckIcon },
          { name: t('nav.newBurialBooking'), href: '/bookings/burial-plot', icon: ArchiveBoxIcon },
          { name: t('nav.providers'), href: '/providers', icon: UserGroupIcon },
          { name: t('nav.familyTree'), href: '/family-tree', icon: Square3Stack3DIcon },
          { name: 'My Will', href: '/will', icon: DocumentCheckIcon },
          { name: 'Wallet', href: '/wallet', icon: WalletIcon },
          { name: t('nav.souvenirShop'), href: '/products', icon: ShoppingBagIcon },
          { name: t('nav.myOrders'), href: '/my-orders', icon: ClipboardDocumentListIcon },
          { name: t('nav.profile'), href: '/profile', icon: UserCircleIcon },
        ]
      case 'FINANCE':
        // Finance's only workspace is the VA Balance page.
        return [
          { name: t('nav.financeVa', 'VA Balance'), href: '/finance', icon: CurrencyDollarIcon },
          { name: t('nav.profile'), href: '/profile', icon: UserCircleIcon },
        ]
      default:
        return [
          { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon },
          { name: t('nav.providers'), href: '/providers', icon: UserGroupIcon },
          { name: t('nav.profile'), href: '/profile', icon: UserCircleIcon },
        ]
    }
  }

  const allNavigation = getNavigationForRole()

  const isActive = (href?: string, isSubmenu?: boolean, submenuItems?: any[]) => {
    // If href is undefined (like for submenu parents), check if any submenu is active
    if (!href) {
      if (isSubmenu && submenuItems) {
        return submenuItems.some((item: any) => location.pathname === item.href)
      }
      return false
    }

    // Handle provider dashboard root path
    if (href === '/provider-dashboard') {
      return location.pathname === '/provider-dashboard'
    }
    // Handle family dashboard root path
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    // Handle admin dashboard root path - only exact match, not startsWith
    if (href === '/admin') {
      return location.pathname === '/admin'
    }
    // For provider management submenu, check if any submenu is active
    if (isSubmenu && submenuItems) {
      return submenuItems.some((item: any) => location.pathname === item.href)
    }
    // For other admin routes, use exact match
    if (href.startsWith('/admin/')) {
      return location.pathname === href
    }

    // Special handling for booking creation routes - only exact match
    if (href === '/bookings/ambulance' || href === '/bookings/burial-plot') {
      return location.pathname === href
    }

    // For other routes, use startsWith
    return location.pathname.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-neutral-200 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4">
            <div className="flex items-center">
              <img src={akhiratLogo} alt="Akhirat" className="h-10 w-10 object-contain" />
              <div className="ml-3">
                <h1 className="text-xl font-bold text-neutral-900">{brandWordmark}</h1>
              </div>
            </div>
          </div>

          <nav className="mt-8 flex-1 px-2 space-y-1">
            {allNavigation.map((item: any) => {
              const Icon = item.icon
              const isExpanded = expandedMenus.includes(item.name)
              const hasSubmenu = item.isSubmenu && item.submenuItems

              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`
                        group flex items-center justify-between w-full px-2 py-2 text-sm font-medium rounded-md transition-colors
                        ${isActive(item.href, item.isSubmenu, item.submenuItems)
                          ? 'bg-primary-100 text-primary-900'
                          : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3 h-5 w-5" />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="mt-1 ml-6 space-y-1">
                        {item.submenuItems.map((subItem: any) => {
                          const SubIcon = subItem.icon
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`
                                group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                                ${location.pathname === subItem.href
                                  ? 'bg-primary-100 text-primary-900'
                                  : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900'
                                }
                              `}
                            >
                              <SubIcon className="mr-3 h-4 w-4" />
                              {subItem.name}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                    ${isActive(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                    }
                  `}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              )
            })}
          </nav>

          {/* User menu */}
          <div className="flex-shrink-0 flex flex-col border-t border-neutral-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1">
                <div className="flex-shrink-0">
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium text-sm">
                        {user?.firstName?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 truncate capitalize">
                    {user?.role?.replace('_', ' ').toLowerCase()}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="ml-2 p-2 rounded-md text-neutral-400 hover:text-neutral-500 hover:bg-neutral-100"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Language Switcher */}
            <div className="pt-2 border-t border-neutral-200">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation bar - visible on both desktop and mobile */}
        <div className="sticky top-0 z-30 bg-white border-b border-neutral-200">
          <div className="flex items-center justify-between px-3 py-2 sm:px-6 lg:px-8 gap-2 h-14 lg:h-auto">
            {/* Mobile: Logo + brand */}
            <div className="lg:hidden flex items-center gap-2 flex-1">
              <img src={akhiratLogo} alt="Akhirat" className="h-7 w-7 object-contain" />
              <h1 className="text-sm font-bold text-neutral-900">{brandWordmark}</h1>
            </div>

            {/* Mobile: menu button (opens drawer) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden flex items-center gap-2 px-3.5 py-2 border-[1.5px] border-neutral-900 rounded-full text-[13px] font-semibold text-neutral-900"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
            >
              {t('navbar.menu')}
              <span className="grid gap-[3.5px]">
                <i className="block w-[15px] h-[1.6px] bg-current rounded" />
                <i className="block w-[15px] h-[1.6px] bg-current rounded" />
                <i className="block w-[15px] h-[1.6px] bg-current rounded" />
              </span>
            </button>

            {/* Desktop: Page title and welcome message */}
            <div className="hidden lg:flex items-center gap-4">
              <h1 className="text-xl font-semibold text-neutral-900">
                {allNavigation.find(item => isActive(item.href))?.name ||
                 (location.pathname.startsWith('/provider-dashboard') ? t('nav.providerDashboard') : t('nav.dashboard'))}
              </h1>
              <span className="text-xs sm:text-sm text-neutral-500">
                {t('dashboard.welcome', { name: user?.firstName })}
              </span>
            </div>

            {/* Cart icon with live count (buyer roles) */}
            {(user?.role === 'FAMILY' || user?.role === 'ADMIN') && (
              <>
                <Link
                  to="/my-orders"
                  className="relative p-2 rounded-md text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
                  aria-label={t('nav.myOrders')}
                  title={t('nav.myOrders')}
                >
                  <ClipboardDocumentListIcon className="h-6 w-6" />
                </Link>
                <Link
                  to="/cart"
                  className="relative p-2 rounded-md text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100"
                  aria-label={t('nav.cart')}
                  title={t('nav.cart')}
                >
                  <ShoppingCartIcon className="h-6 w-6" />
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 rounded-full bg-primary-600 text-white text-[11px] font-semibold flex items-center justify-center">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6 pb-24 lg:pb-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Mobile drawer — dark off-canvas */}
      {drawerRendered && (
        <div
          className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
            drawerShown ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <div
            onClick={() => setMobileMenuOpen(false)}
            className="absolute inset-0 bg-[#10231A]/50 backdrop-blur-[3px]"
          />
          <aside
            className={`absolute top-0 right-0 bottom-0 w-[86%] max-w-[400px] flex flex-col bg-gradient-to-b from-[#0E2018] via-[#123024] to-[#0E2018] text-[#F2EFE6] shadow-[-30px_0_70px_-30px_rgba(16,35,26,0.55)] transform transition-transform duration-500 ease-out ${
              // Always mounted while animating — `hidden` would kill the
              // transition. Unmount happens 500ms after close (above).
              drawerShown ? 'translate-x-0' : 'translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            {/* subtle eight-point star pattern */}
            <div
              className="absolute inset-0 opacity-5 pointer-events-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Cg fill='none' stroke='%23C2A14D' stroke-width='1'%3E%3Cpath d='M60 8 L70 50 L112 60 L70 70 L60 112 L50 70 L8 60 L50 50 Z'/%3E%3C/g%3E%3C/svg%3E\")",
                backgroundSize: '130px',
              }}
            />

            {/* Header */}
            <div className="relative flex items-center justify-between px-6 py-5 border-b border-white/15">
              <span className="font-display tracking-[0.16em] text-[16px]">{brandWordmark}</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className="w-9 h-9 rounded-full grid place-items-center border border-white/25 hover:bg-white/10 hover:rotate-90 transition-all duration-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* User */}
            <div className="relative flex items-center gap-3 px-6 py-4 border-b border-white/10">
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#C2A14D] grid place-items-center flex-shrink-0">
                  <span className="text-[#171509] font-bold">{user?.firstName?.charAt(0) || 'U'}</span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-[#F2EFE6]/55 capitalize truncate">
                  {user?.role?.replace('_', ' ').toLowerCase()}
                </p>
              </div>
            </div>

            {/* Nav */}
            <nav className="relative flex-1 overflow-y-auto px-3 py-3 space-y-1">
              {allNavigation.map((item: any) => {
                const Icon = item.icon
                const isExpanded = expandedMenus.includes(item.name)
                const hasSubmenu = item.isSubmenu && item.submenuItems

                if (hasSubmenu) {
                  const groupActive = isActive(item.href, item.isSubmenu, item.submenuItems)
                  return (
                    <div key={item.name}>
                      <button
                        onClick={() => toggleMenu(item.name)}
                        className={`group w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-[15px] transition-colors ${
                          groupActive ? 'bg-white/10 text-[#C2A14D]' : 'text-[#F2EFE6]/85 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <Icon className="h-5 w-5 opacity-80" />
                          {item.name}
                        </span>
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4 text-[#C2A14D]" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-[#F2EFE6]/40" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="mt-1 ml-9 pl-3 border-l border-[#C2A14D]/25 space-y-0.5">
                          {item.submenuItems.map((subItem: any) => {
                            const SubIcon = subItem.icon
                            const subActive = location.pathname === subItem.href
                            return (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                className={`flex items-center gap-2.5 px-2 py-2 rounded-lg text-[13.5px] transition-colors ${
                                  subActive ? 'text-[#C2A14D]' : 'text-[#F2EFE6]/65 hover:text-white'
                                }`}
                              >
                                <SubIcon className="h-4 w-4 opacity-80" />
                                {subItem.name}
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                }

                const active = isActive(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] transition-colors ${
                      active
                        ? 'bg-white/10 text-[#C2A14D] border-l-2 border-[#C2A14D]'
                        : 'text-[#F2EFE6]/85 border-l-2 border-transparent hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5 opacity-80" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>

            {/* Footer: language + logout */}
            <div className="relative border-t border-white/15 px-6 py-4 space-y-3">
              <LanguageSwitcher variant="dark" />
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2.5 text-sm font-medium text-red-300 hover:bg-red-500/10 rounded-xl transition-colors"
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                Logout
              </button>
            </div>
          </aside>
        </div>
      )}

    </div>
  )
}

export default Layout
